import { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { skillAssessmentWorkStatus } from "const/constants";
import { formatDisplayDateTime } from "utils/dateFormat";
import {
  addSkillAssessmentStatusEntry,
  useSkillAssessmentStatusHistory,
} from "../skillAssessmentStatusHistoryApi";

const CREATION_STATUS = "Application Created";

const statusFilter = createFilterOptions({
  stringify: (option) => option?.label || option?.value || "",
});

const formatHistoryDate = (value) => formatDisplayDateTime(value);

const getApiError = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const SkillAssessmentStatusHistoryTab = ({ skillAssessmentId }) => {
  const queryClient = useQueryClient();
  const { data: history = [], isLoading } = useSkillAssessmentStatusHistory(skillAssessmentId);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");

  const statusOptions = useMemo(() => {
    const map = new Map();
    skillAssessmentWorkStatus.forEach((item) => map.set(item.label.toLowerCase(), item));
    history.forEach((entry) => {
      const label = entry?.toStatus?.trim();
      if (label && label !== CREATION_STATUS && !map.has(label.toLowerCase())) {
        map.set(label.toLowerCase(), { label, value: label });
      }
    });
    return Array.from(map.values());
  }, [history]);

  const mutation = useMutation({
    mutationFn: () =>
      addSkillAssessmentStatusEntry(skillAssessmentId, {
        toStatus: status.trim(),
        note: note.trim() || null,
      }),
    onSuccess: () => {
      toast.success("Status recorded");
      setStatus("");
      setNote("");
      queryClient.invalidateQueries({
        queryKey: ["skill-assessment-status-history", skillAssessmentId],
      });
    },
    onError: (err) => toast.error(getApiError(err, "Failed to record status")),
  });

  const submit = () => {
    if (!status.trim()) {
      toast.error("Select or add a status");
      return;
    }
    mutation.mutate();
  };

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
          Add status update
        </Typography>
        <Box className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Autocomplete
            freeSolo
            options={statusOptions}
            filterOptions={(options, params) => {
              const filtered = statusFilter(options, params);
              const inputValue = params.inputValue.trim();
              const isExisting = options.some(
                (option) => option.label.toLowerCase() === inputValue.toLowerCase()
              );
              if (inputValue !== "" && !isExisting) {
                filtered.push({ inputValue, label: `Add "${inputValue}"`, value: inputValue });
              }
              return filtered;
            }}
            value={statusOptions.find((item) => item.label === status) || status || null}
            onChange={(_, newValue) => {
              if (typeof newValue === "string") setStatus(newValue.trim());
              else if (newValue?.inputValue) setStatus(newValue.inputValue.trim());
              else setStatus(newValue?.label?.trim() || "");
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === "input") setStatus(newInputValue);
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              if (option?.inputValue) return option.inputValue;
              return option?.label || "";
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  {option.inputValue ? `Add "${option.inputValue}"` : option.label}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                size="small"
                required
                placeholder="Select or add a status"
              />
            )}
          />
          <TextField
            label="Note (optional)"
            size="small"
            className="md:col-span-2"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Additional context for this update"
          />
        </Box>
        <Button
          variant="contained"
          onClick={submit}
          disabled={mutation.isLoading}
          sx={{ mt: 2 }}
          className="ml-auto"
        >
          {mutation.isLoading ? "Submitting…" : "Submit status update"}
        </Button>
      </Paper>

      <Stack spacing={1.5}>
        {isLoading ? (
          <Typography variant="body2" color="text.secondary" className="py-6 text-center">
            Loading status history…
          </Typography>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" className="py-6 text-center">
            No status history yet. Add the first update above.
          </Typography>
        ) : (
          history.map((entry) => (
            <Paper key={entry.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={entry.toStatus} size="small" color="primary" />
                {entry.fromStatus && (
                  <Typography variant="caption" color="text.secondary">
                    from {entry.fromStatus}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatHistoryDate(entry.createdAt)}
                </Typography>
                {entry.actorName && (
                  <Typography variant="caption" color="text.secondary">
                    — {entry.actorName}
                  </Typography>
                )}
              </Stack>
              {entry.note && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {entry.note}
                </Typography>
              )}
            </Paper>
          ))
        )}
      </Stack>
    </Stack>
  );
};

export default SkillAssessmentStatusHistoryTab;
